// hooks/useOrderDetail.ts
import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { shipOrder } from '../lib/shipOrder';
import { OrderDetail } from '../types/order';

export const useOrderDetail = () => {
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOrderDetail = useCallback(async (orderId: string) => {
    setLoading(true);
    setError(null);
    try {
      // Fetch order base data
      const { data: orderData, error: orderError } = await supabase
        .from('order_details')
        .select('*')
        .eq('id', orderId)
        .single();

      if (orderError) throw orderError;

      // Fetch status history
      const { data: statusHistory, error: historyError } = await supabase
        .from('order_status_history')
        .select('*')
        .eq('order_id', orderId)
        .order('created_at', { ascending: true });

      if (historyError) throw historyError;

      const orderDetail: OrderDetail = {
        ...orderData,
        status_history: statusHistory || [],
      };

      setOrder(orderDetail);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateOrderStatus = useCallback(async (
    orderId: string,
    status: string,
    notes?: string,
    trackingNumber?: string,
  ): Promise<{ ok: boolean; emailSent?: boolean; emailError?: string | null }> => {
    try {
      setError(null);

      if (status === 'shipped') {
        const result = await shipOrder({
          orderId,
          trackingNumber: trackingNumber || '',
          previousStatus: order?.status || '',
          notes,
        });
        await fetchOrderDetail(orderId);
        return { ok: true, ...result };
      }

      const updates: Record<string, unknown> = { status };
      if (trackingNumber) updates.tracking_number = trackingNumber;

      const { error: updateError } = await supabase
        .from('orders')
        .update(updates)
        .eq('id', orderId);

      if (updateError) throw updateError;

      const { error: historyError } = await supabase
        .from('order_status_history')
        .insert({
          order_id: orderId,
          status,
          notes
        });

      if (historyError) throw historyError;

      await fetchOrderDetail(orderId);
      return { ok: true };
    } catch (err: any) {
      setError(err.message);
      return { ok: false };
    }
  }, [fetchOrderDetail, order]);

  return {
    order,
    loading,
    error,
    fetchOrderDetail,
    updateOrderStatus
  };
};